<template>
  <div class="col-12 flex justify-center mt2 flex-column">
    <h3 class="center violet h4">Choisis ton terrain</h3>

    <div v-if="!loading" class="col-12">
      <!-- day picker -->
      <div class="col-12 light-grey-bg mt2 p2">
        <div
          v-if="!fromProductStep"
          class="col-12 flex items-stretch court-date-bar"
        >
          <button
            type="button"
            class="court-date-nav btn p1 white-bg dark-grey-bd border-radius-3 flex items-center justify-center"
            :class="{ disabled: isBefore(selectedDate, minDate) || isSameDay(selectedDate, minDate) }"
            aria-label="Jour précédent"
            @click="goBack()"
          >
            <i class="icon icon-angle-left-icon bold"></i>
          </button>
          <div class="court-date-field-wrap flex flex-auto items-stretch">
            <div class="court-date-picker-root w-full">
              <DatePicker
                v-model="selectedDate"
                mode="date"
                :masks="{ input: 'DD/MM/YYYY' }"
                :min-date="minDate"
                :timezone="siteInfo?.timezone || 'Europe/Paris'"
                :popover="courtDatePopover"
                :update-on-input="true"
                :input-debounce="400"
                @update:model-value="onDateModelUpdate"
                @dayclick=" (day, event) => { event.target.blur(); } "
              >
                <template #default="{ inputValue, inputEvents }">
                  <input
                    class="court-date-input col-12 py2 px3 center h4 white-bg dark-grey-bd border-radius-3"
                    type="text"
                    autocomplete="off"
                    :value="inputValue"
                    v-on="inputEvents"
                  >
                </template>
              </DatePicker>
            </div>
          </div>
          <button
            type="button"
            class="court-date-nav btn p1 white-bg dark-grey-bd border-radius-3 flex items-center justify-center"
            aria-label="Jour suivant"
            @click="goFoward()"
          >
            <i class="icon icon-angle-right-icon bold"></i>
          </button>
        </div>

        <div v-if="fromProductStep" class="mt1 mb2">
          <span class="h5">{{ sessionDateLabel }}</span>
          <div class="flex flex-wrap">
            <span
              class="cust-border px1 h7 light-cyan-bg mr1 mt1"
              style="background-color: transparent;"
              :style="{ '--cust-border-color': 'black', '--cust-border-radius': '999px', height: '24px', 'padding-top': '0.20rem' }"
            >
              {{ sessionTime }}
            </span>
          </div>
        </div>
        <SessionHeader v-else-if="selectedSession" :session="selectedSession" background-color="light-grey-bg" />

        <div v-for="(session, index) in sessions" :key="index" @click="selectTerrain(index)">
          <div class="pointer light-cyan-bg col-12 p2 my1 card-top border flex justify-between h6" :class="{'grey-bg disabled': session.placesDisponibles <= 0}">
            <div class="flex items-center">
              {{ session.detail.name }} {{ showSessionDate ? ` - ${formatSessionDate(session)}` : '' }}
            </div>
            <div>
              <div v-if="session.placesDisponibles > 0" class="flex justify-center items-center">
                <div class="cust-border white-bg" :style="{ '--cust-border-color': 'var(--black)', '--cust-border-radius': '999px' }">
                  <span :class="['custom-radio-circle flex white-bg', { 'selected-item-circle': isSelectedTerrain(index) }]"></span>
                </div>
              </div>
              <div v-else class="flex justify-center items-center icon-border black-bg white p1 cust-border h6" :style="{ '--icon-border-color': 'black', '--cust-border-radius': '999px' }">
                <span>COMPLET</span>
              </div>
            </div>
          </div>
        <!-- <div>
          <SessionHeader v-if="isSelectedTerrain(index)" :session="session" background-color="light-grey-bg" />
        </div> -->
        </div>

        <div v-if="sessions.length === 0" class="flex justify-center items-center h5 mt3">
          <span>Aucune session disponible pour cette date</span>
        </div>
      </div>

      <StepAction text-size="h6" :disabled="selectedTerrainIndex === null" @handleClickAction="handleSessionStep" />
    </div>
    <Loader v-else />
  </div>
</template>

<script setup>

import { onBeforeMount, computed, ref, inject, watch } from 'vue';
import { startOfDay, endOfDay, isBefore, isSameDay, subDays, addDays, isAfter } from 'date-fns';
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import { useRoute } from 'vue-router';
import { useStore } from '@/store/index';

import SessionHeader from '@/components/terrain/SessionHeader.vue';
import StepAction from '@/components/terrain/StepAction.vue';
import Loader from '@/components/elements/Loader.vue';

import { getServiceByReference, getSessionsByInterval, getServicesProduct } from '@/utils/requests';
import { frenchTime } from '@/utils/utils';
import { computeSession } from '@/utils';
import { computeCourtDataLayer } from '@/utils/index';

const store = useStore();
const route = useRoute();

/** v-calendar defaults use hover-focus; product wants calendar only after click. */
const courtDatePopover = {
  placement: 'bottom',
  visibility: 'click'
};

const advanceStep = inject('advanceStep');

const offerInfo = computed(() => store.state.offerInfo);
const siteInfo = computed(() => store.state.siteInfo);

/** GraphQL returns one object `{ serviceId }`; buildFilter expects an array. */
const normalizeProductServices = svc => {
  if (svc == null) {
    return [];
  }
  if (Array.isArray(svc)) {
    return svc;
  }
  if (typeof svc === 'object' && svc !== null && ('serviceId' in svc || 'service_id' in svc)) {
    return [svc];
  }
  return [];
};
const isInternalSession = computed(() => !!store.state.siteInfo?.is_internal_session);
const selectedSession = computed(() => store.state.courtSettings.session);

/** Exclude a slot reserved by someone else: captain set, no stock, or a contract with no booking yet. */
const isReservedSlot = s => {
  if (s.captain || (s.placesDisponibles != null && s.placesDisponibles <= 0)) {
    return true;
  }
  const hasContract = (s.participantsAmount || 0) > 0;
  const hasBooking = (s.participants || []).length > 0;
  return hasContract && !hasBooking;
};

const sessions = ref([]);
const loading = ref(false);
const selectedDate = ref(new Date());
const sessionServices = ref([]);

const minDate = computed(() => startOfDay(new Date()));

const isInvite = computed(() => store.state.courtSettings.isInvite);

const sessionDate = computed(() => (sessions.value[0] ? frenchTime(sessions.value[0]?.start_date || new Date(), 'EEEE dd MMMM yyyy', siteInfo.value?.timezone || 'Europe/Paris') : ''));
const sessionDateLabel = computed(() => String(sessionDate.value).charAt(0).toUpperCase() + String(sessionDate.value).slice(1));

const fromProductStep = computed(() => store.state.courtSettings.fromProductStep);

const selectedTerrainIndex = ref(null);

const selectTerrain = index => {
  selectedTerrainIndex.value = index;

  const element = document.getElementById('stepper-actions');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }

  store.commit('setCourtSettings', { ...store.state.courtSettings, session: sessions.value[index] });
};

const formatSessionDate = session => `${frenchTime(session.start_date, "'de' HH:mm", siteInfo.value?.timezone || 'Europe/Paris')} à ${frenchTime(session.end_date, 'HH:mm', siteInfo.value?.timezone || 'Europe/Paris')}`;

const isSelectedTerrain = index => selectedTerrainIndex.value === index;

const handleSessionStep = async () => {
  loading.value = true;
  computeCourtDataLayer('CourtSessions', { ...store.state.courtSettings }, route.fullPath, { ...store.state.product }, { ...store.state.offerInfo });

  // get all product reference for session services
  const { session } = store.state.courtSettings;

  const services = [...new Set(session.detail.products)];

  const response = await getServicesProduct(services);
  store.commit('setCourtSettings', { ...store.state.courtSettings, sessionServices: response });
  loading.value = false;

  advanceStep();
};

const getSessions = async () => {
  loading.value = true;

  const day = selectedDate.value instanceof Date
    ? selectedDate.value
    : new Date(selectedDate.value);
  if (Number.isNaN(day.getTime())) {
    sessions.value = [];
    loading.value = false;
    return;
  }

  const startDate = isAfter(minDate.value, day) ? startOfDay(day) : day;
  const endDate = endOfDay(day);

  const rawSvc = sessionServices.value && sessionServices.value.service;
  const productServices = normalizeProductServices(rawSvc);
  const comptage = siteInfo.value?.code_site_comptage;
  const hasComptage = comptage !== undefined && comptage !== null && comptage !== '';

  if (!productServices.length || !hasComptage) {
    sessions.value = [];
    loading.value = false;
    return;
  }

  const params = {
    startDate,
    endDate,
    codeSiteComptage: comptage,
    productServices,
    isInternalSession: isInternalSession.value
  };

  const response = await getSessionsByInterval(params);
  const list = Array.isArray(response?.sessions) ? response.sessions : [];
  sessions.value = list.map(s => {
    const capacity = s.detail.capacity !== null ? s.detail.capacity : s.detail.max_participant;
    const stock = (capacity || 0) - s.participantsAmount;
    let placesDisponibles = stock >= 0 ? stock : 0;

    if (s.detail.isTerrainSession) {
      placesDisponibles = s.participantsAmount && !isInvite.value ? 0 : placesDisponibles;
    }

    return {
      ...s,
      placesDisponibles
    };
  });

  sessions.value = sessions.value.sort((a, b) => {
    if (isInternalSession.value && a.start_date !== b.start_date) {
      return a.start_date - b.start_date;
    }
    const compare = a.activity.name.localeCompare(b.activity.name);
    if (compare !== 0) {
      return compare;
    }
    return a.start_date - b.start_date;
  });

  selectedTerrainIndex.value = selectedSession.value ? sessions.value.findIndex(s => s.detail.id === selectedSession.value.detail.id) : null;

  loading.value = false;
};

/** Sync emitted value then reload (v-model can lag one tick vs. handler). */
const onDateModelUpdate = async val => {
  if (val == null) return;
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) {
    return;
  }
  selectedDate.value = d;
  await getSessions();
};

const goBack = async () => {
  selectedDate.value = startOfDay(subDays(selectedDate.value, 1));
  await getSessions();
};

const goFoward = async () => {
  selectedDate.value = startOfDay(addDays(selectedDate.value, 1));
  await getSessions();
};

const sessionTime = computed(() => (sessions.value[0] ? frenchTime(sessions.value[0]?.start_date, 'HH:mm', siteInfo.value?.timezone || 'Europe/Paris') + frenchTime(sessions.value[0]?.end_date, ' à HH:mm', siteInfo.value?.timezone || 'Europe/Paris') : ''));

const showSessionDate = ref(false);

watch(selectedDate, () => {
  store.commit('setCourtSettings', { ...store.state.courtSettings, selectedDate: selectedDate.value });
});

onBeforeMount(async () => {
  loading.value = true;

  selectedDate.value = store?.state?.courtSettings?.session?.start_date ? new Date(store?.state?.courtSettings?.session?.start_date) : new Date();

  if (offerInfo.value) {
    sessionServices.value = await getServiceByReference(offerInfo.value.horanet_product.horanet_product_reference);

    store.commit('setCourtSettings', { ...store.state.courtSettings, services: sessionServices.value.service, selectedDate: selectedDate.value });

    if (!fromProductStep.value) {
      await getSessions();
      showSessionDate.value = true;
      return;
    }
    const items = store.state.sessions?.items;
    sessions.value = (Array.isArray(items) ? items : [])
      .filter(s => !isReservedSlot(s))
      .map(s => computeSession(s, isInternalSession.value))
      .sort((a, b) => (a.detail?.name || '').localeCompare(b.detail?.name || ''));
  }

  loading.value = false;
});

</script>

<style scoped>
.court-date-bar {
  gap: 0.5rem;
  width: 100%;
}

.court-date-nav {
  flex: 0 0 2.75rem;
  min-height: 2.75rem;
  align-self: stretch;
}

.court-date-field-wrap {
  flex: 1 1 0%;
  min-width: 0;
}

.court-date-picker-root {
  display: block;
  width: 100%;
  min-height: 2.75rem;
}

.court-date-input {
  cursor: text;
  min-height: 2.75rem;
  width: 100%;
  font-weight: 600;
  color: var(--black, #111);
  letter-spacing: 0.02em;
}

.court-date-input:focus {
  outline: 2px solid rgba(100, 108, 255, 0.45);
  outline-offset: 1px;
}

/* Popover + caret come from v-calendar (.vc-popover-caret); keep above sessions list */
:deep(.vc-popover-content-wrapper) {
  z-index: 20;
}
</style>
